import { getMultipleItemsAvailability } from "@/core/api/graphql/account/queries/getMultipleItemsAvailability";
import { useCurrency } from "@/core/composables";
import { ValidationErrorObjectType } from "@/core/enums";
import {
  algoliaDynamicPropertyName as _algoliaDynamicPropertyName,
  useThorlabsAlgoliaAnalytics,
} from "@/shared/algolia-analytics/algolia-analytics";
import { hasGlobalStock } from "@/shared/thorlabs/shared/functions/stock";
import type {
  ShortCartFragment,
  LineItemTypeExtension,
  OrderLineItemTypeExtension,
  ValidationErrorType,
  ProductAvailabilityRequest,
  MoneyType,
  InputDynamicPropertyValueType,
  VariationTypeExtension,
} from "@/core/api/graphql/types";
import type { ItemForAddBulkItemsToCartResultsModalType } from "@/shared/cart";

const { currentCurrency } = useCurrency();

// Re-export the Algolia dynamic property name constant
export const algoliaDynamicPropertyName = _algoliaDynamicPropertyName;

/** @deprecated No longer used. Add to cart mutations now return ID instead. */
export function getLineItemValidationErrorsGroupedBySKU(
  errors: ValidationErrorType[] = [],
): Record<string, ValidationErrorType[]> {
  const result: Record<string, ValidationErrorType[]> = {};

  errors?.forEach((error) => {
    const sku = error.objectId!;

    result[sku] = result[sku] || [];
    result[sku].push(error);
  });

  return result;
}

export function getItemsForAddBulkItemsToCartResultsModal(
  inputItems: OrderLineItemTypeExtension[] | LineItemTypeExtension[],
  cart: ShortCartFragment,
): ItemForAddBulkItemsToCartResultsModalType[] {
  return inputItems.map<ItemForAddBulkItemsToCartResultsModalType>((item) => ({
    productExists: !!item.product,
    productId: item.productId,
    name: item.name,
    sku: item.sku,
    quantity: item.quantity,
    slug: item.product?.slug,
    isAddedToCart: !cart.validationErrors.some(
      (error) => error.objectType == ValidationErrorObjectType.CatalogProduct && error.objectId === item.productId,
    ),
  }));
}

export async function getAvailability(items: LineItemTypeExtension[] | OrderLineItemTypeExtension[]) {
  const availabilityMap = items.map((item) => {
    const clientCurrency = currentCurrency.value.code;
    const localWarehouse = item.product?.availabilityData.inventories.find((i) =>
      i.fulfillmentCenterName.toLocaleLowerCase().includes(clientCurrency.toLocaleLowerCase()),
    );
    const localStock = localWarehouse ? localWarehouse.inStockQuantity : 0;
    const heavyItem = item.product?.ukHvyTL ? item.product?.ukHvyTL : false;
    const lightweightTables = false;
    const productQuantity = item.quantity;
    const globalWarehouses = item.product?.availabilityData.inventories.filter(
      (i) => !i.fulfillmentCenterName.toLocaleLowerCase().includes(clientCurrency.toLocaleLowerCase()),
    );

    const variation = item.product as VariationTypeExtension;
    const globallyAvailableStock =
      variation && variation.mainProductId !== null
        ? hasGlobalStock(variation, clientCurrency)
          ? 1
          : 0
        : localWarehouse
          ? localWarehouse.backorderQuantity
          : 0;

    const usWarehouse = globalWarehouses?.find((w) => w.fulfillmentCenterName.toLocaleLowerCase().includes("usd"));
    const isBrl = usWarehouse && clientCurrency === "BRL" && productQuantity > localStock;

    const warehouse = isBrl ? "USD" : "";
    const regionalStock = isBrl ? usWarehouse.inStockQuantity : 0;

    return <ProductAvailabilityRequest>{
      clientCurrency,
      warehouse,
      heavyItem,
      productQuantity,
      globallyAvailableStock,
      localStock,
      lightweightTables,
      regionalStock,
      productId: item.id,
    };
  });

  if (availabilityMap === null || availabilityMap === undefined) {
    return null;
  }

  const result = await getMultipleItemsAvailability(availabilityMap);

  // Apply serialized availability logic only for products with variations
  if (result) {
    result.forEach((availability) => {
      const item = items.find((i) => i.id === availability.productId);

      if (!item) {
        return;
      }

      // Special availability rules for configurable items
      if (
        item.product?.code === "CHROLIS" ||
        item.product?.code === "4P3-CUSTOM" ||
        item.product?.code === "4P4-CUSTOM" ||
        item.product?.code === "POLARIS-19S-CUSTOM" ||
        item.product?.code === "PLS-P-CUSTOM" ||
        item.product?.code === "PLS-H-CUSTOM"
      ) {
        availability.availabilityResponse!.availability =
          item.product.descriptionsWithFallbacksTL.find(
            (desc) => desc.reviewType === `Availability${currentCurrency.value.code}`,
          )?.content ??
          item.product.descriptionsWithFallbacksTL.find((desc) => desc.reviewType === "AvailabilityUSD")?.content ??
          "";
      }
      if (item.product?.code === "Patch-Cable-CUSTOM" && item.customDescriptionTL?.indexOf("; Ships: ") !== -1) {
        availability.availabilityResponse!.availability =
          item.customDescriptionTL?.substring(item.customDescriptionTL.indexOf("; Ships: ") + 9) ?? "";
      }
    });
  }

  return result;
}

export function getProductFamilyPageUrl(
  item: LineItemTypeExtension | OrderLineItemTypeExtension,
  checkDiscontinued = false,
) {
  if (checkDiscontinued) {
    const isDiscontinued =
      item.product?.itemStatusTL?.toLowerCase() === "discontinued" ||
      item.product?.itemStatusTL?.toLowerCase() === "superseded";

    if (isDiscontinued) {
      return `/item/${item.product?.altItemIdTL == null ? item.sku : item.product?.altItemIdTL}`;
    }
  }

  let url = item.product?.mainProductId
    ? item.product?.masterVariation?.descriptionsWithFallbacksTL?.find((x) => x.reviewType === "FamilyPageUrl")?.content
    : item.product?.descriptionsWithFallbacksTL?.find((x) => x.reviewType === "FamilyPageUrl")?.content;

  if (url) {
    url += `?pn=${item.product?.mainProductId ? item.product?.masterVariation?.code : item.product?.code}`;
  }

  return url;
}

type ItemWithImageFieldsType = {
  imageUrl?: string | null;
  product?: {
    imgSrc?: string | null;
    images?: Array<{ url: string }> | null;
    masterVariation?: { images?: Array<{ url: string }> | null } | null;
  } | null;
};

export function getLineItemImageSrc(item: ItemWithImageFieldsType | undefined): string | undefined {
  return (
    item?.imageUrl ||
    item?.product?.imgSrc ||
    item?.product?.images?.[0]?.url ||
    item?.product?.masterVariation?.images?.[0]?.url ||
    undefined
  );
}

export function lineItemIsCustomPatchCable(item: LineItemTypeExtension): boolean {
  return item.customDescriptionTL?.startsWith("Custom Patch Cable") ?? false;
}

export function getProductPrice(item: LineItemTypeExtension, isAdaptedToQuantity = false): MoneyType {
  if (!isAdaptedToQuantity && item.product?.isConfigurable) {
    return item.listPrice;
  }
  const currency = item.product?.isConfigurable ? "USD" : currentCurrency.value.code;
  const currentCurrencyPrices = item.product?.allCurrenciesPricesTL
    .filter((x) => x.list.currency.code === currency)
    .sort((a, b) => (a.minQuantity ?? 1) - (b.minQuantity ?? 1));
  const currentCurrencyBasePrice = currentCurrencyPrices?.at(currentCurrencyPrices.length - 1);
  return currentCurrencyBasePrice?.list.amount
    ? isAdaptedToQuantity
      ? item.listPriceAdaptedToQuantityTL
      : createPriceWithNewAmount(currentCurrencyBasePrice.list.amount, item.listPrice)
    : createPriceWithNewAmount(0, item.listPrice);
}

export function createPriceWithNewAmount(newAmount: number, oldItem: MoneyType): MoneyType {
  return {
    amount: newAmount,
    currency: oldItem?.currency,
    formattedAmount: oldItem?.formattedAmount,
    decimalDigits: oldItem?.decimalDigits,
    formattedAmountWithoutCurrency: oldItem?.formattedAmountWithoutCurrency,
    formattedAmountWithoutPoint: oldItem?.formattedAmountWithoutPoint,
    formattedAmountWithoutPointAndCurrency: oldItem?.formattedAmountWithoutPointAndCurrency,
  };
}

/**
 * Get Algolia dynamic properties for cart items
 * Returns dynamic properties if the user is coming from Algolia search
 */
export function getAlgoliaDynamicProperties(): InputDynamicPropertyValueType[] | undefined {
  const { isFromAlgoliaSearch, currentAlgoliaQueryID, currentAlgoliaQueryIndex } = useThorlabsAlgoliaAnalytics();

  const dynamicProperties: InputDynamicPropertyValueType[] | undefined = isFromAlgoliaSearch.value
    ? [
        {
          name: algoliaDynamicPropertyName,
          value: `${currentAlgoliaQueryID.value}~${currentAlgoliaQueryIndex.value}`,
        },
      ]
    : undefined;

  return dynamicProperties;
}
